/** Administração da plataforma — tipos e chamadas, separados do `endpoints.ts`. */

import { api } from './client';
import type { PapelDeAcesso } from '../types';

export interface LojaAdministrada {
  id: number;
  slug: string;
  subdominio: string | null;
  nome: string;
  nome_curto: string | null;
  numero: string | null;
  oriente: string | null;
  uf: string | null;
  potencia: string | null;
  rito: string | null;
  data_fundacao: string | null;
  cnpj: string | null;
  email_contato: string | null;
  telefone: string | null;
  endereco: string | null;
  ativo: boolean;
  criado_em: string;
  brasao_url: string | null;
  irmaos_ativos: number;
  acessos_ativos: number;
  administradores: number;
  ultimo_acesso_em: string | null;
  /** É a loja de quem está olhando — não pode ser desativada por ele. */
  e_a_minha: boolean;
  assinatura: ResumoAssinatura | null;
}

// ── Assinatura ──────────────────────────────────────────────────────────────
export type SituacaoAssinatura = 'cortesia' | 'teste' | 'a_combinar' | 'em_dia' | 'em_atraso' | 'suspensa';
export type FormaPagamentoAssinatura = 'pix' | 'transferencia' | 'boleto' | 'dinheiro' | 'outro';

export interface ResumoAssinatura {
  situacao: SituacaoAssinatura;
  valor: string | null;
  dia_vencimento: number | null;
  teste_ate: string | null;
  cortesia: boolean;
  proximo_vencimento: string | null;
  em_atraso_desde: string | null;
  meses_em_aberto: number;
  valor_em_aberto: string;
  pago_ate: string | null;
  /** Ativos no quadro, sem licenciados nem isentos — é o que define a faixa. */
  irmaos_na_conta: number;
  faixa_sugerida: string;
  /** Nulo quando a faixa é "sob consulta". */
  valor_sugerido: string | null;
}

export interface PagamentoAssinatura {
  id: number;
  competencia: string;
  valor: string;
  pago_em: string;
  forma: FormaPagamentoAssinatura;
  observacao: string | null;
  registrado_por: string;
  criado_em: string;
}

export interface AssinaturaDetalhe extends ResumoAssinatura {
  inicio: string | null;
  primeira_competencia: string | null;
  observacao: string | null;
  competencias_em_aberto: string[];
  pagamentos: PagamentoAssinatura[];
}

export interface AssinaturaEntrada {
  valor: string | null;
  dia_vencimento: number | null;
  teste_ate: string | null;
  inicio: string | null;
  cortesia: boolean;
  observacao: string | null;
}

// ── Cobrança: todas as lojas, mês a mês ─────────────────────────────────────
export type EstadoDoMes = 'pago' | 'a_vencer' | 'atrasado' | 'teste' | 'nao_devida' | 'cortesia' | 'a_combinar';

export interface MesDaLoja {
  competencia: string;
  estado: EstadoDoMes;
  vencimento: string | null;
  pagamento: PagamentoAssinatura | null;
}

export interface LinhaDeCobranca {
  id: number;
  slug: string;
  nome: string;
  nome_curto: string | null;
  numero: string | null;
  ativo: boolean;
  brasao_url: string | null;
  assinatura: ResumoAssinatura;
  meses: MesDaLoja[];
}

export interface TotalDoMes {
  competencia: string;
  esperado: string;
  recebido: string;
  lojas_pagas: number;
  lojas_em_aberto: number;
}

export interface Cobranca {
  hoje: string;
  competencias: string[];
  lojas: LinhaDeCobranca[];
  totais: TotalDoMes[];
}

export interface PagamentoEntrada {
  competencia: string;
  valor: string;
  pago_em: string;
  forma: FormaPagamentoAssinatura;
  observacao: string | null;
}

export interface DadosDaLoja {
  slug: string;
  /** Whitelabel: endereço próprio da loja (só o rótulo, ex.: lauromuller). */
  subdominio: string | null;
  nome: string;
  nome_curto: string | null;
  numero: string | null;
  oriente: string | null;
  uf: string | null;
  potencia: string | null;
  rito: string | null;
  data_fundacao: string | null;
  cnpj: string | null;
  email_contato: string | null;
  telefone: string | null;
  endereco: string | null;
}

export interface NovoAcesso {
  /** Preenchido: acesso de um irmão do quadro (entra pelo CIM). */
  membro_id?: number | null;
  nome_completo?: string | null;
  email?: string | null;
  usuario?: string | null;
  senha: string;
  papeis: string[];
}

export interface AcessoAdministrado {
  id: number;
  usuario: string;
  email: string;
  nome_completo: string;
  membro_id: number | null;
  cim: string | null;
  papeis: string[];
  ativo: boolean;
  bloqueado: boolean;
  bloqueado_ate: string | null;
  ultimo_login_em: string | null;
  trocar_senha_no_proximo_login: boolean;
  administra_plataforma: boolean;
  sessoes_ativas: number;
  /** É quem está olhando: não revoga o próprio acesso nem a própria plataforma. */
  e_voce: boolean;
}

export interface AcessoEntradaPlataforma {
  papeis: string[];
  ativo: boolean;
  administra_plataforma: boolean;
}

export interface IrmaoSemAcesso {
  id: number;
  nome: string;
  cim: string;
  email: string | null;
}

const base = '/plataforma';

export const ApiPlataforma = {
  papeis: async () => (await api.get<PapelDeAcesso[]>(`${base}/papeis`)).data,
  lojas: async () => (await api.get<LojaAdministrada[]>(`${base}/lojas`)).data,
  loja: async (id: number) => (await api.get<LojaAdministrada>(`${base}/lojas/${id}`)).data,
  criarLoja: async (dados: DadosDaLoja & { primeiro_acesso: NovoAcesso }) =>
    (await api.post<LojaAdministrada>(`${base}/lojas`, dados)).data,
  editarLoja: async (id: number, dados: DadosDaLoja & { ativo: boolean }) =>
    (await api.put<LojaAdministrada>(`${base}/lojas/${id}`, dados)).data,

  enviarBrasao: async (id: number, arquivo: File) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    // Sem Content-Type explícito: o navegador precisa gerar o boundary.
    return (await api.put<LojaAdministrada>(`${base}/lojas/${id}/brasao`, form)).data;
  },
  removerBrasao: async (id: number) =>
    (await api.delete<LojaAdministrada>(`${base}/lojas/${id}/brasao`)).data,

  /** `ate`: qualquer dia do último mês mostrado (aaaa-mm-dd). */
  cobranca: async (ate?: string, meses = 6) =>
    (await api.get<Cobranca>(`${base}/cobranca`, { params: { ate, meses } })).data,
  assinatura: async (lojaId: number) =>
    (await api.get<AssinaturaDetalhe>(`${base}/lojas/${lojaId}/assinatura`)).data,
  definirAssinatura: async (lojaId: number, dados: AssinaturaEntrada) =>
    (await api.put<AssinaturaDetalhe>(`${base}/lojas/${lojaId}/assinatura`, dados)).data,
  registrarPagamento: async (lojaId: number, dados: PagamentoEntrada) =>
    (await api.post<AssinaturaDetalhe>(`${base}/lojas/${lojaId}/assinatura/pagamentos`, dados)).data,
  excluirPagamento: async (lojaId: number, pagamentoId: number) =>
    (await api.delete<AssinaturaDetalhe>(`${base}/lojas/${lojaId}/assinatura/pagamentos/${pagamentoId}`)).data,
  /** ZIP com todos os dados da loja (LGPD: portabilidade e cancelamento). */
  exportar: async (lojaId: number) =>
    (await api.get<Blob>(`${base}/lojas/${lojaId}/exportacao`, { responseType: 'blob' })).data,

  acessos: async (lojaId: number) =>
    (await api.get<AcessoAdministrado[]>(`${base}/lojas/${lojaId}/acessos`)).data,
  irmaosSemAcesso: async (lojaId: number) =>
    (await api.get<IrmaoSemAcesso[]>(`${base}/lojas/${lojaId}/irmaos-sem-acesso`)).data,
  conceder: async (lojaId: number, dados: NovoAcesso) =>
    (await api.post<AcessoAdministrado>(`${base}/lojas/${lojaId}/acessos`, dados)).data,
  definirAcesso: async (lojaId: number, usuarioId: number, dados: AcessoEntradaPlataforma) =>
    (await api.put<AcessoAdministrado>(`${base}/lojas/${lojaId}/acessos/${usuarioId}`, dados)).data,
  definirSenha: async (lojaId: number, usuarioId: number, senha: string) =>
    (
      await api.put<AcessoAdministrado>(`${base}/lojas/${lojaId}/acessos/${usuarioId}/senha`, {
        senha,
        trocar_no_proximo_login: true,
      })
    ).data,
  desbloquear: async (lojaId: number, usuarioId: number) =>
    (await api.post<AcessoAdministrado>(`${base}/lojas/${lojaId}/acessos/${usuarioId}/desbloquear`))
      .data,
  encerrarSessoes: async (lojaId: number, usuarioId: number) =>
    (
      await api.post<AcessoAdministrado>(
        `${base}/lojas/${lojaId}/acessos/${usuarioId}/encerrar-sessoes`,
      )
    ).data,
};
